import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, PictureAsPdf, AddAPhoto } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { jsPDF } from "jspdf";
import logo from "../assets/logo.png";
import dayjs from "dayjs";
import colors from "../config/colors";
import auth from "../services/auth";
import { savePartesDiarias, getPartesDiarias } from "../services/source";

// const FILE_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// const resolveFileUrl = (path) => {
//   const cleanedPath = String(path || "").replace(/^\/+/, "");
//   if (!cleanedPath) return "#";

//   if (/^https?:\/\//i.test(cleanedPath)) {
//     return cleanedPath;
//   }

//   if (FILE_BASE_URL) {
//     return `${FILE_BASE_URL}/${cleanedPath}`;
//   }

//   return `${window.location.origin}/${cleanedPath}`;
// };

const PartesDiarias = ({ companyId }) => {
  const authUser = auth.getCurrentUser();
  const resolvedCompanyId =
    companyId || authUser?.data?.companyId || authUser?.data?.id || "";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogSeverity, setDialogSeverity] = useState("success");
  const [savedParte, setSavedParte] = useState(null);
  const [, setPartes] = useState([]);
  const [, setLoadingPartes] = useState(false);
  const [, setPartesError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSavedParte(null);
  }, []);

  const showDialog = useCallback(
    (message, severity = "success", parte = null) => {
      setDialogMessage(message || "Mensagem desconhecida");
      setDialogSeverity(severity);
      setSavedParte(parte);
      setDialogOpen(true);
    },
    [],
  );

  const getInitialValues = () => ({
    nomeCliente: "",
    horaEntrada: "",
    horaSaida: "",
    horas: "",
    servico: "",
    observacao: "",
    companyId: companyId || "",
  });

  const validationSchema = Yup.object({
    nomeCliente: Yup.string().required("Nome do cliente obrigatório"),
    horaEntrada: Yup.string()
      .required("Hora entrada obrigatória")
      .test(
        "hora-valida",
        "Hora entrada deve ser menor que hora saída",
        function (value) {
          const { horaSaida } = this.parent;
          if (!value || !horaSaida) {
            return true;
          }
          return value < horaSaida;
        },
      ),
    horaSaida: Yup.string()
      .required("Hora saída obrigatória")
      .test(
        "hora-valida",
        "Hora saída deve ser maior que hora entrada",
        function (value) {
          const { horaEntrada } = this.parent;
          if (!value || !horaEntrada) {
            return true;
          }
          return value > horaEntrada;
        },
      ),
    servico: Yup.string().required("Serviço desenvolvido obrigatório"),
  });

  const calculateHours = (entrada, saida) => {
    if (!entrada || !saida) {
      return "";
    }

    const start = new Date(`1970-01-01T${entrada}:00`);
    const end = new Date(`1970-01-01T${saida}:00`);
    let diff = (end - start) / 1000 / 60;

    if (diff < 0) {
      diff += 24 * 60;
    }

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}min`;
  };

  const loadPartesDiarias = useCallback(async () => {
    if (!resolvedCompanyId) {
      setPartes([]);
      return;
    }

    try {
      setLoadingPartes(true);
      setPartesError("");
      const response = await getPartesDiarias(resolvedCompanyId, "", "");
      if (response?.success) {
        setPartes(Array.isArray(response.data) ? response.data : []);
      } else {
        setPartes([]);
        setPartesError(response?.message || "Erro ao carregar partes diárias.");
      }
    } catch (err) {
      console.error("Erro ao carregar partes diárias:", err);
      setPartes([]);
      setPartesError("Erro ao carregar partes diárias.");
    } finally {
      setLoadingPartes(false);
    }
  }, [resolvedCompanyId]);

  useEffect(() => {
    loadPartesDiarias();
  }, [loadPartesDiarias]);

  useEffect(() => {
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    setSelectedFiles((prev) => [...prev, ...files]);
    event.target.value = null;
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const formData = new FormData();
    formData.append("nomeCliente", values.nomeCliente);
    formData.append("horaEntrada", values.horaEntrada);
    formData.append("horaSaida", values.horaSaida);
    formData.append("horas", values.horas || "");
    formData.append("servico", values.servico);
    formData.append("observacao", values.observacao || "");
    formData.append("companyId", values.companyId || resolvedCompanyId || "");

    selectedFiles.forEach((file, index) => {
      formData.append("fotos[]", file, file.name);
    });

    try {
      const response = await savePartesDiarias(formData);
      if (response?.success) {
        const parteData = {
          nomeCliente: values.nomeCliente,
          horaEntrada: values.horaEntrada,
          horaSaida: values.horaSaida,
          horas: values.horas || "",
          servico: values.servico,
          observacao: values.observacao || "",
          data: dayjs().format("DD/MM/YYYY"),
        };

        showDialog(
          response?.message || "Parte diária guardada com sucesso!",
          "success",
          parteData,
        );
        resetForm({ values: getInitialValues() });
        setSelectedFiles([]);
        loadPartesDiarias();
      } else {
        showDialog(
          response?.message || "Erro ao guardar a parte diária.",
          "error",
        );
      }
    } catch (error) {
      console.error("Erro ao guardar parte diária:", error);
      showDialog(
        error?.response?.data?.message || "Erro ao guardar a parte diária.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const handleGeneratePdf = useCallback(async () => {
    if (!savedParte) return;

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginLeft = 40;
    const maxWidth = pageWidth - marginLeft * 2;
    const lineHeight = 16;

    let y = 25;

    try {
      const img = await loadImage(logo);

      const logoWidth = 100;
      const logoHeight = (img.height / img.width) * logoWidth;

      const logoX = pageWidth - logoWidth - 20;

      doc.addImage(img, "PNG", logoX, y, logoWidth, logoHeight);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("PARTE DIÁRIA", marginLeft, y + logoHeight / 2 + 6);

      y += logoHeight + 20;
    } catch (error) {
      console.warn("Não foi possível carregar o logo do PDF", error);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("PARTE DIÁRIA", marginLeft, y);
      y += 30;
    }

    doc.setFontSize(11);

    const fields = [
      {
        label: "Cliente:",
        value: savedParte.nomeCliente || "-",
      },
      {
        label: "Data:",
        value: savedParte.data || dayjs().format("DD/MM/YYYY"),
      },
      {
        label: "Hora Entrada:",
        value: savedParte.horaEntrada || "-",
      },
      {
        label: "Hora Saída:",
        value: savedParte.horaSaida || "-",
      },
      {
        label: "Total:",
        value: savedParte.horas || "-",
      },
    ];

    fields.forEach(({ label, value }) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, marginLeft, y);
      doc.setFont("helvetica", "normal");
      const split = doc.splitTextToSize(String(value), maxWidth - 100);
      doc.text(split, marginLeft + 100, y);
      y += split.length * lineHeight + 4;
    });

    y += 10;

    const detailFields = [
      {
        label: "Serviço:",
        value: savedParte.servico || "-",
      },
      {
        label: "Observação:",
        value: savedParte.observacao || "-",
      },
    ];

    detailFields.forEach(({ label, value }) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, marginLeft, y);
      doc.setFont("helvetica", "normal");
      const split = doc.splitTextToSize(String(value), maxWidth - 100);
      doc.text(split, marginLeft + 100, y);
      y += split.length * lineHeight + 8;
    });

    y += 40;

    const signatureWidth = 180;

    doc.line(marginLeft, y, marginLeft + signatureWidth, y);
    doc.setFontSize(10);
    doc.text("Assinatura Técnico", marginLeft + signatureWidth / 2, y + 15, {
      align: "center",
    });

    const clientX = pageWidth - marginLeft - signatureWidth;

    doc.line(clientX, y, clientX + signatureWidth, y);
    doc.text("Assinatura Cliente", clientX + signatureWidth / 2, y + 15, {
      align: "center",
    });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.text(
      `Gerado em ${dayjs().format("DD/MM/YYYY HH:mm")}`,
      pageWidth / 2,
      pageHeight - 20,
      { align: "center" },
    );

    const url = doc.output("bloburl");
    window.open(url, "_blank", "noopener,noreferrer");
  }, [savedParte]);

  return (
    <Box>
      <Card
        elevation={4}
        sx={{
          borderRadius: 4,
          border: "1px solid #e7e9f1",
          background:
            "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,249,253,1) 100%)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" fontWeight="bold" mb={3}>
            Parte Diária
          </Typography>

          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome Cliente"
                      name="nomeCliente"
                      value={values.nomeCliente}
                      onChange={handleChange}
                      error={touched.nomeCliente && Boolean(errors.nomeCliente)}
                      helperText={touched.nomeCliente && errors.nomeCliente}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Hora Entrada"
                      name="horaEntrada"
                      value={values.horaEntrada}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) => {
                        handleChange(event);
                        setFieldValue(
                          "horas",
                          calculateHours(event.target.value, values.horaSaida),
                        );
                      }}
                      error={touched.horaEntrada && Boolean(errors.horaEntrada)}
                      helperText={touched.horaEntrada && errors.horaEntrada}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Hora Saída"
                      name="horaSaida"
                      value={values.horaSaida}
                      InputLabelProps={{ shrink: true }}
                      onChange={(event) => {
                        handleChange(event);
                        setFieldValue(
                          "horas",
                          calculateHours(
                            values.horaEntrada,
                            event.target.value,
                          ),
                        );
                      }}
                      error={touched.horaSaida && Boolean(errors.horaSaida)}
                      helperText={touched.horaSaida && errors.horaSaida}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Total Trabalhado"
                      value={values.horas}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Serviço Desenvolvido"
                      name="servico"
                      value={values.servico}
                      onChange={handleChange}
                      error={touched.servico && Boolean(errors.servico)}
                      helperText={touched.servico && errors.servico}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Observação"
                      name="observacao"
                      value={values.observacao}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AddAPhoto />}
                      sx={{
                        textTransform: "none",
                        borderRadius: 3,
                        borderColor: colors.secondary,
                        color: colors.secondary,
                      }}
                    >
                      Fotos
                      <input
                        hidden
                        accept="image/*"
                        multiple
                        type="file"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {selectedFiles.length > 0
                        ? `${selectedFiles.length} ficheiro(s) selecionado(s)`
                        : "Nenhuma foto selecionada"}
                    </Typography>

                    {previewUrls.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        {previewUrls.map((url, index) => (
                          <Box
                            key={url}
                            sx={{
                              position: "relative",
                              width: 96,
                              height: 96,
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "1px solid #d1d5db",
                            }}
                          >
                            <Box
                              component="img"
                              src={url}
                              alt={selectedFiles[index]?.name || "Foto"}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedFiles((prev) =>
                                  prev.filter((_, idx) => idx !== index),
                                );
                              }}
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                bgcolor: "rgba(255,255,255,0.85)",
                                p: 0.5,
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: 3,
                        px: 5,
                        backgroundColor: colors.secondary,
                      }}
                    >
                      {isSubmitting ? "A guardar..." : "Guardar Parte Diária"}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          {dialogSeverity === "success" ? "Sucesso" : "Erro"}
        </DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          {savedParte ? (
            <Button
              startIcon={<PictureAsPdf />}
              onClick={handleGeneratePdf}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: colors.secondary,
                color: colors.secondary,
                borderRadius: 5,
              }}
            >
              Gerar PDF
            </Button>
          ) : null}
          <Button
            onClick={handleDialogClose}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 5 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartesDiarias;
